// Angular
import { ChangeDetectionStrategy, Component, forwardRef, Injector, ViewChild } from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
// BCDFX - Globals
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// Services
// Components
// Models
import { ICDSpinnerModel } from "../../data-idc/controls/icd-spinner.model";
// Enums - ICD Coordination
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
// Enums - ATDL
import { ATDLNParameterType } from "../../data-atdl/enums/atdl-parameter-type.enum";
// Enums
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";

import { LoggerService } from "@bfx/logger";
import { RateSpinnerComponent } from "shared/base-components/rate-spinner.component";
import { NumberSpinnerComponent, ZERO } from "shared/base-components/number-spinner.component";
import { ICDBaseSpinnerComponent } from "./icd-base-spinner.component";
import { ICD_BASE_DEFAULT_HOST_RECORD } from "./icd-base.component";
import { ICD_ID_LIMIT_PRICE, RATE } from "../oe-panel-algo-params.const";
import { RateSpinnerPairConstants } from "../../../../shared/base-components/rate-spinner.constants";

const logger = LoggerService.for(__dirname);

export const DYN_RATE_SPINNER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICDRateSpinnerComponent),
  multi: true,
};

@Component({
  selector: "icd-rate-spinner",
  host: {
    ...ICD_BASE_DEFAULT_HOST_RECORD,
    "[class.icd__base__spinner__container]": "true",
  },
  templateUrl: "./html/icd-rate-spinner.html",
  providers: [DYN_RATE_SPINNER_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICDRateSpinnerComponent extends ICDBaseSpinnerComponent {
  // CONSTRAINTS
  public productCode: string = null;
  public inverted: boolean = false;

  /************************************/
  @ViewChild("rate_spinner", { static: true }) private rateSpinner: RateSpinnerComponent;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(injector: Injector) {
    super(injector);
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */
  protected initializeICDSpecificModelProperties(): boolean {
    const value = super.initializeICDSpecificModelProperties();

    if (
      this.icdId.toLowerCase() == ICD_ID_LIMIT_PRICE &&
      (this.required || (this.auxCheckbox.enabled && this.auxCheckbox.value))
    ) {
      this.dynRespond(ICDNResponseCommand.REQUEST_RATE);
    }

    return value;
  }
  protected override handleConstraintUpdate(data: any): void {
    let dynResponses = new Map<ICDNResponseCommand, any>();
    this.cd.markForCheck();

    if (
      data instanceof Object &&
      data.hasOwnProperty("value") &&
      data.hasOwnProperty("name") &&
      typeof data.name === "string"
    ) {
      if (data.name.toUpperCase() === "ccyPairCode".toUpperCase()) {
        if (typeof data.value === "string" && data.value.length === 6) {
          this.productCode = data.value;
        } else {
          logger.out.error(
            `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Constraint '${data.name}' payload has invalid data.value! Data dump: %O`,
            data.value,
          );
        }
      } else if (data.name.toUpperCase() === "ccyPairInverted".toUpperCase()) {
        if (typeof data.value === "boolean") {
          this.inverted = data.value;
        } else {
          logger.out.error(
            `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Constraint '${data.name}' payload has invalid data.value! Data dump: %O`,
            data.value,
          );
        }
      } else if (data.name.toUpperCase() === RATE.toUpperCase()) {
        if (this.subData.initValue == null || ZERO.isEqualTo(this.subData.initValue)) {
          // as per KD if the control has an initial value the rate is not set.
          this.outputExtendedLogging(`ICD [${this.icdId}] Received rate: `, data.value);
          if (data.value instanceof CubiqTS.BigNumber) {
            // For rates, we always set the default to last selected pair rate
            this.subData.defaultValue = data.value;

            if (
              this.required ||
              !(this.locked || (this.disabled && this.auxCheckbox.enabled == false))
            ) {
              this.setAndUpdateValue(data.value);
              dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
            }
          } else {
            logger.out.error(
              `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Constraint '${data.name}' payload has invalid data.value! Data dump: %O`,
              data.value,
            );
          }
        }
      } else {
        // STATE FLOW RULES
        dynResponses = this.processExternalStateFlowDependency(data, dynResponses);
      }
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Invalid constraint data! Data dump: %O`,
        data,
      );
    }

    this.handleDynResponses(dynResponses);
  }

  /**********************************************************************/
  /* EXTERNAL API */

  public override load(value: ICDSpinnerModel, propagate?: boolean): void {
    let dynResponses = new Map();

    if (value instanceof ICDSpinnerModel || value === null) {
      this.set(value);
      this.updateValue();
      this.validate();

      // fix for rate overwrite on modify and duplicate (https://www.bcdfx.com/ijira/browse/OMS-28830)
      if (
        this.data.state !== ICNInputControlState.INVALID &&
        this.subData.numValue instanceof CubiqTS.BigNumber &&
        value.skipInitValue !== true
      ) {
        this.subData.initValue = this.subData.numValue;
      }

      dynResponses.set(ICDNResponseCommand.LOAD_COMPLETE, null);

      dynResponses = this.processAuxStateFlow(dynResponses);

      if (typeof propagate === "boolean" && propagate) {
        dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
      }

      this.handleDynResponses(dynResponses);
    }
  }

  protected override getDefaultLabel(): string {
    return "Rate Spinner";
  }
  protected override getAllowedTypes(): ATDLNParameterType[] {
    return [ATDLNParameterType.PRICE, ATDLNParameterType.PRICE_OFFSET, ATDLNParameterType.FLOAT];
  }

  protected override get numberSpinner(): NumberSpinnerComponent {
    return this.rateSpinner;
  }

  protected override initialiseConfiguration(): void {
    this.config.precision = 4;
    this.config.minValue = ZERO;
    this.config.maxValue = null;
    this.config.emptyValue = ZERO;
    this.config.minValueSet = true;
    this.config.maxValueSet = false;
    this.config.allowNegativeNum = false;
  }

  public onAuxCheckboxChange(): void {
    if (this.icdId.toLowerCase() == ICD_ID_LIMIT_PRICE) {
      if (this.auxCheckbox.value) {
        this.dynRespond(ICDNResponseCommand.REQUEST_RATE);
      }
      this.data.value = null;
      this.data.numValue = null;
      this.setAndUpdateValue(this.data.value);
      this.dynRespond(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
    }

    super.onAuxCheckboxChange();
  }

  public additionalSpinnerValidation(): boolean {
    return true;
  }

  public onPairConstantsChange($event: RateSpinnerPairConstants) {
    this.config.precision = $event.maxPrecision;
    this.initConfigMinValue();
    logger.out.info(`${this.constructor.name}.onPairConstantsChange(%o)`, $event);
    this.validateIC();
  }
}
